import type { MaybeRefOrGetter } from 'vue';
import type { FieldSelection, Filter, GlobalCollectionId, ResolvedMonospaceCollection } from '#layers/data-model/public';
import type { QuerySort } from '#shared/types/rstore';
import { refThrottled } from '@vueuse/core';
import { computed, ref, toValue, watch } from 'vue';
import { useStore } from '#imports';
import { useProcessedFieldSelection } from '#layers/access/public';
import { isDeepEqual } from '#layers/base/public';
import { extractPrimitiveFilterFieldNames, getPrimitiveFieldSelection, getRelationFieldSelection, isRelationalFilter, usePaginatedQuery } from '#layers/data-model/public';
import { DEFAULT_LISTING_PAGE_SIZE } from '#layers/ui/public';
import { useWorkspaceSchemaStore, useWorkspaceStore } from '#layers/workspace/public';

export interface ReadManyQuery {
	fields?: FieldSelection;
	filter?: Filter;
	sort?: QuerySort;
	limit?: number;
	offset?: number;
}

interface UseItemsQuery extends ReadManyQuery {
	search?: string;
}

interface UseItemsOptions {
	collection: MaybeRefOrGetter<ResolvedMonospaceCollection | GlobalCollectionId | undefined>;
	query?: MaybeRefOrGetter<UseItemsQuery>;
	/**
	 * Indicates if the query should run.
	 *
	 * TODO understand if this should also disable the updating of the items composable
	 * 		in the current state `items` might return an old value, and still react to
	 * 		changes in the filter and collection.
	 *
	 * @default true
	 */
	enabled?: MaybeRefOrGetter<boolean>;
	/**
	 * Page size override, if not provided the collection defaultPageSize is used
	 */
	pageSize?: MaybeRefOrGetter<number | undefined>;
	/**
	 * When true, sets a permission error if explicitly requested fields are not permitted.
	 * When false (default), non-permitted fields are silently excluded.
	 */
	errorOnNonPermittedField?: MaybeRefOrGetter<boolean>;
	/**
	 * Throttle network fetches while the query changes rapidly (e.g. typing a filter value),
	 * relying on client-side cache filtering in between.
	 *
	 * Disable for layouts whose query only changes in discrete steps (e.g. the calendar's
	 * view/range switches), where throttling adds latency without coalescing anything.
	 *
	 * @default true
	 */
	throttle?: MaybeRefOrGetter<boolean>;
}

export function useItems(options: UseItemsOptions) {
	const { collection, query, pageSize, enabled = true, errorOnNonPermittedField = false, throttle = true } = options;

	const { currentWorkspaceSlug } = useWorkspaceStore();
	const { findCollectionById } = useWorkspaceSchemaStore();
	const store = useStore();

	const computedCollection = computed(() => {
		const collectionOrId = toValue(collection);

		if (typeof collectionOrId === 'string') {
			return findCollectionById(collectionOrId);
		}
		else {
			return collectionOrId;
		}
	});

	const computedQuery = computed(() => toValue(query));
	const throttledQuery = refThrottled(computed(() => toValue(query)), 500);

	const computedEnabled = computed(() => toValue(enabled));

	const queryFields = computed<FieldSelection | undefined>(() => computedQuery.value?.fields ?? ['*']);

	const queryFieldsWithFilterFields = computed<FieldSelection | undefined>(() => {
		const fields = queryFields.value;
		if (!fields || fields.includes('*')) return fields;

		const filter = computedQuery.value?.filter;
		if (!filter) return fields;

		const filterFieldNames = extractPrimitiveFilterFieldNames(filter);
		if (filterFieldNames.length === 0) return fields;

		const merged = new Set(getPrimitiveFieldSelection(fields));
		const relations = getRelationFieldSelection(fields);
		const sizeBefore = merged.size;

		for (const name of filterFieldNames) {
			merged.add(name);
		}

		if (merged.size === sizeBefore) return fields;
		return [...merged, ...relations];
	});

	const { fields: processedFields, excluded } = useProcessedFieldSelection({
		collection: computedCollection,
		fields: queryFieldsWithFilterFields,
	});

	const permissionError = computed(() => {
		if (!toValue(errorOnNonPermittedField)) return null;
		if (excluded.value.length === 0) return null;
		return new Error(`Fields not permitted: ${excluded.value.join(', ')}`);
	});

	const computedPageSize = computed(() => toValue(pageSize) ?? DEFAULT_LISTING_PAGE_SIZE);

	const currentPageNumber = ref(1);

	const readManyQuery = computed<ReadManyQuery>(() => {
		const limit = computedPageSize.value;
		const offset = (currentPageNumber.value - 1) * limit;

		return {
			fields: processedFields.value,
			filter: computedQuery.value?.filter,
			sort: computedQuery.value?.sort,
			limit,
			...(offset > 0 ? { offset } : {}),
		};
	});

	// Reset to first page when page size changes
	watch(computedPageSize, () => {
		currentPageNumber.value = 1;
	}, {
		// Before the query re-runs
		flush: 'sync',
	});

	// Reset to first page when query params change.
	// Default flush is enough because currentPageNumber is not a query dependency.
	watch(computedQuery, (newVal, oldVal) => {
		if (isDeepEqual(newVal, oldVal)) return;
		currentPageNumber.value = 1;
	});

	// we want to delay the network request until the throttled query has caught up with the current query
	const querySettled = computed(() => !toValue(throttle) || isDeepEqual(computedQuery.value, throttledQuery.value));

	const fetchPolicy = computed(() => {
		if (computedQuery.value?.filter && isRelationalFilter(computedQuery.value?.filter)) {
			// we can't locally filter a relational filter and have to rely on the server to do it for us
			return 'no-cache';
		}

		// query is settled (last fetch is long enough ago that we want to _actually_ fetch the data from the backend)
		if (querySettled.value) {
			return 'cache-and-fetch';
		}

		// rely on client only filtering, this will only return items that are already in the client cache
		return 'cache-only';
	});

	const collectionQuery = store.$collection(() => computedCollection.value?.apiName ?? '').query((q) => q.many(computedEnabled.value && currentWorkspaceSlug.value
		? {
				workspaceSlug: currentWorkspaceSlug.value,
				fetchPolicy: fetchPolicy.value,
				// Keep paginated rows tied to the server response while still reading
				// live item values from rstore's cache.
				resultMode: 'responseRefs',
				filter: readManyQuery.value.filter,
				fields: readManyQuery.value.fields,
				sort: readManyQuery.value.sort,
				textSearch: computedQuery.value?.search,
				pageIndex: 0,
				pageSize: computedPageSize.value,
			}
		: { enabled: false }));

	const isSettled = computed(() => !collectionQuery.loading.value && querySettled.value);

	const { items, allItems, currentPage } = usePaginatedQuery({
		collectionQuery,
		pageNumber: currentPageNumber,
		collectionApiName: computed(() => computedCollection.value?.apiName),
		enabled: computedEnabled,
	});

	const totalItemsCount = computed(() => collectionQuery.meta.value?.totalCount);
	const filteredItemsCount = computed(() => collectionQuery.meta.value?.filteredCount);

	const selectedPageStartIndex = computed(() => (currentPageNumber.value - 1) * computedPageSize.value);

	const selectedPageHasKnownItems = computed(() => {
		if (currentPageNumber.value <= 1) return false;

		const totalCount = totalItemsCount.value;
		return totalCount != null && selectedPageStartIndex.value < totalCount;
	});

	// RStore's cache-and-fetch path can expose an empty cache before the
	// background fetch has proven whether the result is actually empty.
	const showLoading = computed(() => {
		if (!computedEnabled.value || !computedCollection.value || !currentWorkspaceSlug.value) return false;
		if (items.value.length > 0) return false;
		if (collectionQuery.error.value) return false;
		if (collectionQuery.loading.value || currentPage.value?.loading || !isSettled.value || selectedPageHasKnownItems.value) return true;
		return totalItemsCount.value == null && filteredItemsCount.value == null;
	});

	function refresh() {
		return collectionQuery.refresh();
	}

	return {
		items,
		allItems,
		isLoading: collectionQuery.loading,
		isEnabled: computedEnabled,
		isSettled,
		showLoading,
		error: collectionQuery.error,
		permissionError,
		pageNumber: currentPageNumber,
		totalItemsCount,
		filteredItemsCount,
		refresh,
		query: readManyQuery,
	};
}
